/**
 * Popup Block - Frontend JavaScript
 *
 * Handles all trigger types:
 *   button      — manual click on .popup-block__trigger
 *   load        — fires after page load + optional delay (seconds)
 *   exit        — fires on exit-intent (mouseleave toward top of viewport)
 *   scroll      — fires when user scrolls past a % of the page
 *   inactivity  — fires after N seconds of no user interaction
 *
 * "Show once" uses a cookie keyed to the popup ID.
 *
 * Add to webpack.config.js entry:
 *   entry['popup'] = path.resolve(__dirname, './blocks/popup/popup-init.js');
 */

(function () {
  "use strict";

  // ─── Cookie helpers ────────────────────────────────────────────────────────

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; expires=" +
      expires +
      "; path=/; SameSite=Lax";
  }

  function getCookie(name) {
    return document.cookie.split("; ").reduce(function (val, part) {
      const [k, v] = part.split("=");
      return k === name ? decodeURIComponent(v) : val;
    }, null);
  }

  function hasSeen(popupId) {
    return getCookie("popup_seen_" + popupId) === "1";
  }

  function markSeen(popupId, days) {
    setCookie("popup_seen_" + popupId, "1", days);
  }

  // ─── Open / close ──────────────────────────────────────────────────────────
  // We toggle the .is-open class only — the overlay stays in the DOM always.
  // This lets CSS transitions (opacity, transform, backdrop-filter) actually run,
  // since display:none would yank elements out of the render tree mid-transition.

  function openPopup(overlay, modal) {
    overlay.classList.add("is-open");
    overlay.setAttribute("aria-hidden", "false");
    document.body.classList.add("popup-is-open");
    modal.focus();
  }

  function closePopup(overlay) {
    overlay.classList.remove("is-open");
    overlay.setAttribute("aria-hidden", "true");
    if (!document.querySelector(".popup-block__overlay.is-open")) {
      document.body.classList.remove("popup-is-open");
    }
  }

  // ─── Focus trap ────────────────────────────────────────────────────────────

  function trapFocus(modal) {
    modal.addEventListener("keydown", function (e) {
      if (e.key !== "Tab") return;
      const focusable = Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  // ─── Trigger: BUTTON ───────────────────────────────────────────────────────

  function initButtonTrigger(overlay, modal) {
    const triggers = document.querySelectorAll(
      '.popup-block__trigger[data-popup-id="' + overlay.id + '"]',
    );
    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function (e) {
        e.preventDefault();
        openPopup(overlay, modal);
      });
    });
  }

  // ─── Trigger: LOAD (with delay) ────────────────────────────────────────────

  function initLoadTrigger(overlay, modal, cfg) {
    if (cfg.showOnce && hasSeen(overlay.id)) return;
    const delayMs = (cfg.loadDelay || 0) * 1000;
    setTimeout(function () {
      openPopup(overlay, modal);
      if (cfg.showOnce) markSeen(overlay.id, cfg.cookieDays);
    }, delayMs);
  }

  // ─── Trigger: EXIT INTENT ──────────────────────────────────────────────────

  function initExitTrigger(overlay, modal, cfg) {
    if (cfg.showOnce && hasSeen(overlay.id)) return;

    let fired = false;

    document.addEventListener("mouseleave", function (e) {
      if (fired) return;
      // Fire when cursor leaves toward the top of the viewport
      if (e.clientY <= (cfg.exitSensitivity || 20)) {
        fired = true;
        openPopup(overlay, modal);
        if (cfg.showOnce) markSeen(overlay.id, cfg.cookieDays);
      }
    });
  }

  // ─── Trigger: SCROLL DEPTH ─────────────────────────────────────────────────

  function initScrollTrigger(overlay, modal, cfg) {
    if (cfg.showOnce && hasSeen(overlay.id)) return;

    const threshold = (cfg.scrollDepth || 50) / 100;
    let fired = false;

    function onScroll() {
      if (fired) return;
      const scrolled = window.scrollY + window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight;
      if (scrolled / totalHeight >= threshold) {
        fired = true;
        openPopup(overlay, modal);
        if (cfg.showOnce) markSeen(overlay.id, cfg.cookieDays);
        window.removeEventListener("scroll", onScroll);
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
  }

  // ─── Trigger: INACTIVITY ───────────────────────────────────────────────────

  function initInactivityTrigger(overlay, modal, cfg) {
    if (cfg.showOnce && hasSeen(overlay.id)) return;

    const idleMs = (cfg.inactivityDelay || 30) * 1000;
    const activityEvents = [
      "mousemove",
      "keydown",
      "scroll",
      "click",
      "touchstart",
    ];
    let timer;
    let fired = false;

    function resetTimer() {
      clearTimeout(timer);
      if (fired) return;
      timer = setTimeout(function () {
        fired = true;
        openPopup(overlay, modal);
        if (cfg.showOnce) markSeen(overlay.id, cfg.cookieDays);
        // Stop listening once fired
        activityEvents.forEach(function (ev) {
          document.removeEventListener(ev, resetTimer);
        });
      }, idleMs);
    }

    activityEvents.forEach(function (ev) {
      document.addEventListener(ev, resetTimer, { passive: true });
    });

    // Start the first timer immediately
    resetTimer();
  }

  // ─── Close behaviours (shared) ─────────────────────────────────────────────

  function initCloseHandlers(overlay, modal, cfg) {
    // Close button
    const closeBtn = overlay.querySelector(".popup-block__close");
    if (closeBtn) {
      closeBtn.addEventListener("click", function () {
        closePopup(overlay);
      });
    }

    // Click outside modal
    if (cfg.closeOnOverlay) {
      overlay.addEventListener("click", function (e) {
        if (e.target === overlay) closePopup(overlay);
      });
    }

    // ESC key
    if (cfg.closeOnEsc) {
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && overlay.classList.contains("is-open")) {
          closePopup(overlay);
        }
      });
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function initPopups() {
    const overlays = document.querySelectorAll(
      ".popup-block__overlay[data-config]",
    );

    overlays.forEach(function (overlay) {
      const modal = overlay.querySelector(".popup-block__modal");
      if (!modal) return;

      let cfg = {};
      try {
        cfg = JSON.parse(overlay.dataset.config);
      } catch (err) {
        console.warn(
          "Popup block: could not parse config for #" + overlay.id,
          err,
        );
        return;
      }

      // Shared close handlers
      initCloseHandlers(overlay, modal, cfg);
      trapFocus(modal);

      // Trigger-specific init
      switch (cfg.triggerType) {
        case "button":
          initButtonTrigger(overlay, modal);
          break;
        case "load":
          initLoadTrigger(overlay, modal, cfg);
          break;
        case "exit":
          initExitTrigger(overlay, modal, cfg);
          break;
        case "scroll":
          initScrollTrigger(overlay, modal, cfg);
          break;
        case "inactivity":
          initInactivityTrigger(overlay, modal, cfg);
          break;
        default:
          console.warn(
            'Popup block: unknown triggerType "' + cfg.triggerType + '"',
          );
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPopups);
  } else {
    initPopups();
  }
})();
